// React
import React, { lazy, Suspense } from "react";

// Third party imports
import { ErrorBoundary } from "react-error-boundary";
import { Navigate, Route, Routes } from "react-router-dom";

// Components
import ErrorFallback from "components/ErrorFallback";
import SuspenseFallback from "components/SuspenseFallback";

// Layouts
import ClientPortalLayout from "layouts/ClientPortalLayout";
import MainLayout from "layouts/MainLayout";

// Utils
import { isClientUser } from "utils/auth.jsx";

import ProtectedRoute from "context/ProtectedRoute.jsx";
// Contexts
import PublicRoute from "context/PublicRoute.jsx";

// Pages
const LoginPage = lazy(() => import("pages/authentication/Login"));
const AliasSelectionPage = lazy(() => import("pages/authentication/AliasSelection"));
const SignupPage = lazy(() => import("pages/authentication/Signup"));
const ForgotPasswordPage = lazy(() => import("pages/authentication/ForgotPassword"));
const ResetPasswordPage = lazy(() => import("pages/authentication/ResetPassword"));
const AccountVerificationPage = lazy(() => import("pages/authentication/AccountVerification"));
const CompleteRegistrationPage = lazy(() => import("pages/authentication/CompleteRegistration"));
const ForbiddenPage = lazy(() => import("pages/errors/Forbidden403"));
const NotFoundPage = lazy(() => import("pages/errors/NotFound404"));
const ClientsPage = lazy(() => import("pages/main/Clients"));
const MarketingMindPage = lazy(() => import("pages/main/MarketingMind"));
const NotificationsPage = lazy(() => import("pages/main/Notifications"));
const AiChatPage = lazy(() => import("pages/ai-chat/AiChatPage"));
const ClientBoardPage = lazy(() => import("pages/client/ClientBoard"));
const ClientSubTasks = lazy(() => import("pages/client/ClientSubTasks"));
const ClientInformation = lazy(() => import("pages/client/ClientInformation"));
const ClientMoodboard = lazy(() => import("pages/client/ClientMoodboard"));
const ClientMarketingMindV2 = lazy(() => import("pages/client/ClientMarketingMindV2"));
const ClientNotes = lazy(() => import("pages/client/ClientNotes"));
const Credentials = lazy(() => import("pages/client/Credentials"));
const Gallery = lazy(() => import("pages/client/Gallery"));
const Archive = lazy(() => import("pages/client/Archive"));
const ClientPayments = lazy(() => import("pages/client/ClientPayments"));
const ClientContract = lazy(() => import("pages/client/ClientContract"));
const ProfilePage = lazy(() => import("pages/profile/ProfilePage"));
const OperatorProfilePage = lazy(() => import("pages/profile/OperatorProfilePage"));
const OperatorBoardPage = lazy(() => import("pages/operator-board/OperatorBoard"));
const OperatorBoardTablePage = lazy(() => import("pages/operator-board/OperatorBoardTable"));
const OperatorBoardCalendarPage = lazy(() => import("pages/operator-board/OperatorBoardCalendar"));
const HelpCenterPage = lazy(() => import("pages/help-center/HelpCenter"));
const MyBoardPage = lazy(() => import("pages/my-board/MyBoard"));
const MyBoardCalendarPage = lazy(() => import("pages/my-board/MyBoardCalendar"));
const SubtaskPage = lazy(() => import("pages/subtask/SubtaskPage"));
const ServicePage = lazy(() => import("pages/service/ServicePage"));
const TaskPage = lazy(() => import("pages/task/TaskPage"));
const SettingsPage = lazy(() => import("pages/settings/SettingsPage"));
const OperatorsSettingsPage = lazy(() => import("pages/settings/OperatorsSettingsPage"));
const ClientsSettingsPage = lazy(() => import("pages/settings/ClientsSettingsPage"));
const ClientAccessSettingsPage = lazy(() => import("pages/settings/ClientAccessSettingsPage"));
const LabelsSettingsPage = lazy(() => import("pages/settings/LabelsSettingsPage"));
const RolesSettingsPage = lazy(() => import("pages/settings/RolesSettingsPage"));
const TeamsSettingsPage = lazy(() => import("pages/settings/TeamsSettingsPage"));
const TeamsDetailsPage = lazy(() => import("pages/settings/TeamsDetailsPage"));
const MasterPlanSettingsPage = lazy(() => import("pages/settings/MasterPlanSettingsPage"));
const MasterPlanDetailsPage = lazy(() => import("pages/settings/MasterPlanDetailsPage"));
const TemplatesSettingsPage = lazy(() => import("pages/settings/TemplatesSettingsPage"));
const BoardPage = lazy(() => import("pages/boards/BoardPage"));
const TeamBoardRedirect = lazy(() => import("pages/boards/TeamBoardRedirect"));
const LinkedBoardPage = lazy(() => import("pages/linked-board/LinkedBoardPage"));

export const PUBLIC_ROUTES = {
  ORGANIZATION_SELECTION: "/organization-selection",
  LOGIN: "/login",
  SET_PASSWORD: (token) => `/set-password/${token}`,
  RESET_PASSWORD: (token) => `/reset-password/${token}`,
  FORGOT_PASSWORD: "/forgot-password",
  ACCOUNT_VERIFICATION: (otp_id) => `/account-verification/${otp_id}`,
  COMPLETE_REGISTRATION: (user_id) => `/complete-registration/${user_id}`,
};
export const PROTECTED_ROUTES = {
  CHANGE_PASSWORD: "/change-password",
  CLIENTS: "/clients",
  // «Marketing Mind»: its roadmap and what every client still misses
  MARKETING_MIND: "/marketing-mind",
  NOTIFICATIONS: "/notifications",
  // Full screen AI assistant chat (same path as components/AiAssistant/aiStore AI_CHAT_PATH)
  AI_CHAT: "/chat",
  PROFILE: "/profile",
  MY_BOARD: "/my-board/board",
  MY_BOARD_TASKS: "/my-board/table",
  MY_BOARD_CALENDAR: "/my-board/calendar",
  OPERATOR_PROFILE: (operator_id) => `/operator/${operator_id}`,
  OPERATOR_BOARD: (operator_id) => `/operator/${operator_id}/board`,
  OPERATOR_BOARD_TABLE: (operator_id) => `/operator/${operator_id}/table`,
  OPERATOR_BOARD_CALENDAR: (operator_id) => `/operator/${operator_id}/calendar`,
  CLIENT_BOARD: (company_id) => `/${company_id}/board`,
  CLIENT_SUBTASKS: (company_id) => `/${company_id}/subtasks`,
  ARCHIVE: (company_id) => `/${company_id}/archive`,
  CLIENT_INFORMATION: (company_id) => `/${company_id}/information`,
  CLIENT_MOODBOARD: (company_id) => `/${company_id}/moodboard`,
  CLIENT_MARKETING_MIND_V2: (company_id) => `/${company_id}/marketing-mind-v2`,
  NOTES: (company_id) => `/${company_id}/notes`,
  CREDENTIALS: (company_id) => `/${company_id}/credentials`,
  GALLERY: (company_id) => `/${company_id}/gallery`,
  COMPANY_PAYMENTS: (company_id) => `/${company_id}/payments`,
  CLIENT_CONTRACT: (company_id) => `/${company_id}/contract`,
  SERVICE: (company_id, id) => `/${company_id}/service/${id}`,
  TASK: (company_id, id) => `/${company_id}/task/${id}`,
  SUBTASK: (company_id, id) => `/${company_id}/subtask/${id}`,
  SETTINGS: "/settings",
  MASTERPLAN_TEMPLATES_SETTINGS: "/settings/masterplan-templates",
  OPERATORS_SETTINGS: "/settings/operators",
  CLIENTS_SETTINGS: "/settings/clients",
  CLIENT_ACCESS_SETTINGS: "/settings/client-access",
  LABELS_SETTINGS: "/settings/labels",
  ROLES_SETTINGS: "/settings/roles",
  TEAMS_SETTINGS: "/settings/teams",
  TEAM_DETAILS_SETTINGS: (team_id) => `/settings/teams/${team_id}`,
  MASTERPLAN_SETTINGS: "/settings/masterplan",
  MASTERPLAN_DETAILS: (service_id) => `/settings/masterplan/details/${service_id}`,
  MASTERPLAN_SUBSERVICE_DETAILS: (service_id, subservice_id) => `/settings/masterplan/details/${service_id}/${subservice_id}`,
  HELP_CENTER: "/help-center",
  TEAM_DASHBOARD: (team_id) => `/team/${team_id}/board`,
  BOARD: (board_id) => `/boards/${board_id}`,
  // Linked boards (Account / Grafici / Strategist): `board` = account | graphics | strategist
  // (own prefix: "/boards/:board_id" is reserved to the configurable team boards)
  LINKED_BOARD: (board) => `/linked-boards/${String(board).toLowerCase()}`,
};
export const OTHER_ROUTES = {
  FORBIDDEN_403: "/403",
  NOT_FOUND_404: "/404",
};
export const APP_ROUTES = {
  ...PROTECTED_ROUTES,
  ...PUBLIC_ROUTES,
  ...OTHER_ROUTES,
};
const ProtectedMainLayout = ({ children }) => {
  // Client (CU) users get a lightweight portal layout without the CRM sidebar/navbar
  const Layout = isClientUser() ? ClientPortalLayout : MainLayout;
  return (
    <ProtectedRoute>
      <Layout>{children}</Layout>
    </ProtectedRoute>
  );
};
export default function Routing() {
  return (
    <Suspense fallback={<SuspenseFallback />}>
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <Routes>
          <Route path="/" element={<Navigate to={PUBLIC_ROUTES.LOGIN} />} />
          <Route path={OTHER_ROUTES.FORBIDDEN_403} element={<ForbiddenPage />} />
          <Route path="*" element={<NotFoundPage />} />
          <Route
            path={PUBLIC_ROUTES.ORGANIZATION_SELECTION}
            element={
              <PublicRoute>
                <AliasSelectionPage />
              </PublicRoute>
            }
          />
          <Route
            path={PUBLIC_ROUTES.LOGIN}
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            }
          />
          <Route
            path="/signup/:token"
            element={
              <PublicRoute>
                <SignupPage />
              </PublicRoute>
            }
          />
          <Route
            path={PUBLIC_ROUTES.FORGOT_PASSWORD}
            element={
              <PublicRoute>
                <ForgotPasswordPage />
              </PublicRoute>
            }
          />
          <Route
            path="/account-verification/:otp_id"
            element={
              <PublicRoute>
                <AccountVerificationPage />
              </PublicRoute>
            }
          />
          <Route
            path="/complete-registration/:user_id"
            element={
              <PublicRoute>
                <CompleteRegistrationPage />
              </PublicRoute>
            }
          />
          <Route
            path="/reset-password/:token"
            element={
              <PublicRoute>
                <ResetPasswordPage />
              </PublicRoute>
            }
          />
          {/* Protected Routes with Main Layout */}
          <Route
            path={PROTECTED_ROUTES.CLIENTS}
            element={
              <ProtectedMainLayout>
                <ClientsPage />
              </ProtectedMainLayout>
            }
          >
            {/* Dashboard */}
          </Route>
          <Route
            path={PROTECTED_ROUTES.MARKETING_MIND}
            element={
              <ProtectedMainLayout>
                <MarketingMindPage />
              </ProtectedMainLayout>
            }
          />
          <Route
            path={PROTECTED_ROUTES.NOTIFICATIONS}
            element={
              <ProtectedMainLayout>
                <NotificationsPage />
              </ProtectedMainLayout>
            }
          />
          <Route
            path={PROTECTED_ROUTES.AI_CHAT}
            element={
              <ProtectedMainLayout>
                <AiChatPage />
              </ProtectedMainLayout>
            }
          />
          <Route
            path={PROTECTED_ROUTES.PROFILE}
            element={
              <ProtectedMainLayout>
                <ProfilePage />
              </ProtectedMainLayout>
            }
          />
          <Route
            path="/operator/:operator_id"
            element={
              <ProtectedMainLayout>
                <OperatorProfilePage />
              </ProtectedMainLayout>
            }
          />
          <Route
            path="/operator/:operator_id/board"
            element={
              <ProtectedMainLayout>
                <OperatorBoardPage />
              </ProtectedMainLayout>
            }
          />
          <Route
            path="/operator/:operator_id/table"
            element={
              <ProtectedMainLayout>
                <OperatorBoardTablePage />
              </ProtectedMainLayout>
            }
          />
          <Route
            path="/operator/:operator_id/calendar"
            element={
              <ProtectedMainLayout>
                <OperatorBoardCalendarPage />
              </ProtectedMainLayout>
            }
          />
          <Route
            path="/:company_id/board"
            element={
              <ProtectedMainLayout>
                <ClientBoardPage />
              </ProtectedMainLayout>
            }
          />
          <Route
            path="/:company_id/subtasks-table"
            element={
              <ProtectedMainLayout>
                <ClientSubTasks />
              </ProtectedMainLayout>
            }
          />
          <Route
            path="/:company_id/information"
            element={
              <ProtectedMainLayout>
                <ClientInformation />
              </ProtectedMainLayout>
            }
          />
          <Route
            path="/:company_id/moodboard"
            element={
              <ProtectedMainLayout>
                <ClientMoodboard />
              </ProtectedMainLayout>
            }
          />
          <Route
            path="/:company_id/marketing-mind-v2"
            element={
              <ProtectedMainLayout>
                <ClientMarketingMindV2 />
              </ProtectedMainLayout>
            }
          />
          <Route
            path="/:company_id/notes"
            element={
              <ProtectedMainLayout>
                <ClientNotes />
              </ProtectedMainLayout>
            }
          />
          <Route
            path="/:company_id/credentials"
            element={
              <ProtectedMainLayout>
                <Credentials />
              </ProtectedMainLayout>
            }
          />
          <Route
            path="/:company_id/gallery"
            element={
              <ProtectedMainLayout>
                <Gallery />
              </ProtectedMainLayout>
            }
          />
          <Route
            path="/:company_id/archive"
            element={
              <ProtectedMainLayout>
                <Archive />
              </ProtectedMainLayout>
            }
          />
          <Route
            path="/:company_id/payments"
            element={
              <ProtectedMainLayout>
                <ClientPayments />
              </ProtectedMainLayout>
            }
          />
          <Route
            path="/:company_id/contract"
            element={
              <ProtectedMainLayout>
                <ClientContract />
              </ProtectedMainLayout>
            }
          />
          <Route
            path="/:company_id/subtask/:id"
            element={
              <ProtectedMainLayout>
                <SubtaskPage />
              </ProtectedMainLayout>
            }
          />
          <Route
            path="/:company_id/service/:id"
            element={
              <ProtectedMainLayout>
                <ServicePage />
              </ProtectedMainLayout>
            }
          />
          <Route
            path="/:company_id/task/:id"
            element={
              <ProtectedMainLayout>
                <TaskPage />
              </ProtectedMainLayout>
            }
          />
          {/* Help Center Route */}
          <Route
            path={PROTECTED_ROUTES.HELP_CENTER}
            element={
              <ProtectedMainLayout>
                <HelpCenterPage />
              </ProtectedMainLayout>
            }
          />
          <Route
            path={PROTECTED_ROUTES.MY_BOARD}
            element={
              <ProtectedMainLayout>
                <MyBoardPage />
              </ProtectedMainLayout>
            }
          />
          <Route
            path={PROTECTED_ROUTES.MY_BOARD_TASKS}
            element={<Navigate to={PROTECTED_ROUTES.MY_BOARD} replace />}
          />
          <Route
            path={PROTECTED_ROUTES.MY_BOARD_CALENDAR}
            element={
              <ProtectedMainLayout>
                <MyBoardCalendarPage />
              </ProtectedMainLayout>
            }
          />
          {/* Settings routes */}
          <Route
            path={PROTECTED_ROUTES.SETTINGS}
            element={
              <ProtectedMainLayout>
                <SettingsPage />
              </ProtectedMainLayout>
            }
          />
          <Route
            path={PROTECTED_ROUTES.OPERATORS_SETTINGS}
            element={
              <ProtectedMainLayout>
                <OperatorsSettingsPage />
              </ProtectedMainLayout>
            }
          />
          <Route
            path={PROTECTED_ROUTES.CLIENTS_SETTINGS}
            element={
              <ProtectedMainLayout>
                <ClientsSettingsPage />
              </ProtectedMainLayout>
            }
          />
          <Route
            path={PROTECTED_ROUTES.CLIENT_ACCESS_SETTINGS}
            element={
              <ProtectedMainLayout>
                <ClientAccessSettingsPage />
              </ProtectedMainLayout>
            }
          />
          <Route
            path={PROTECTED_ROUTES.LABELS_SETTINGS}
            element={
              <ProtectedMainLayout>
                <LabelsSettingsPage />
              </ProtectedMainLayout>
            }
          />
          <Route
            path={PROTECTED_ROUTES.ROLES_SETTINGS}
            element={
              <ProtectedMainLayout>
                <RolesSettingsPage />
              </ProtectedMainLayout>
            }
          />
          <Route
            path={PROTECTED_ROUTES.TEAMS_SETTINGS}
            element={
              <ProtectedMainLayout>
                <TeamsSettingsPage />
              </ProtectedMainLayout>
            }
          />
          <Route
            path="/settings/teams/:team_id"
            element={
              <ProtectedMainLayout>
                <TeamsDetailsPage />
              </ProtectedMainLayout>
            }
          />
          <Route
            path={PROTECTED_ROUTES.MASTERPLAN_SETTINGS}
            element={
              <ProtectedMainLayout>
                <MasterPlanSettingsPage />
              </ProtectedMainLayout>
            }
          />
          <Route
            path={PROTECTED_ROUTES.MASTERPLAN_TEMPLATES_SETTINGS}
            element={
              <ProtectedMainLayout>
                <TemplatesSettingsPage />
              </ProtectedMainLayout>
            }
          />
          <Route
            path="/settings/masterplan/details/:service_id"
            element={
              <ProtectedMainLayout>
                <MasterPlanDetailsPage />
              </ProtectedMainLayout>
            }
          />
          <Route
            path="/settings/masterplan/details/:service_id/:subservice_id"
            element={
              <ProtectedMainLayout>
                <MasterPlanDetailsPage />
              </ProtectedMainLayout>
            }
          />
          {/* Boards ("Bacheche team") */}
          <Route
            path="/boards/:board_id"
            element={
              <ProtectedMainLayout>
                <BoardPage />
              </ProtectedMainLayout>
            }
          />
          {/* Linked boards ("Bacheche": Account / Grafici / Strategist) */}
          <Route
            path="/linked-boards/:board"
            element={
              <ProtectedMainLayout>
                <LinkedBoardPage />
              </ProtectedMainLayout>
            }
          />
          {/* Legacy team board links → the team's board */}
          <Route
            path="/team/:team_id/board"
            element={
              <ProtectedMainLayout>
                <TeamBoardRedirect />
              </ProtectedMainLayout>
            }
          />
        </Routes>
      </ErrorBoundary>
    </Suspense>
  );
}
